import { IBus }                               from '@/Emulator/Bus/IBus';
import { APU, PPU }                           from '@/Emulator/Hardware';
import { Joypad }                             from '@/Emulator/IO/Joypad';
import { IMapper }                            from '@/Emulator/Mapper';
import { deserializeMemory, serializeMemory } from '@/Emulator/Utility';

export class CPUBus implements IBus, ISerializable
{
    static IsAddressing = {
        SYS_RAM:   (addr: number): boolean => addr >= 0x0000 && addr <= 0x1FFF,
        PRG_RAM:   (addr: number): boolean => addr >= 0x6000 && addr <= 0x7FFF,
        ROM:       (addr: number): boolean => addr >= 0x8000 && addr <= 0xFFFF,
        PPU:       (addr: number): boolean => addr >= 0x2000 && addr <= 0x3FFF,
        APU_WRITE: (addr: number): boolean => (addr >= 0x4000 && addr <= 0x4013) || addr === 0x4015 || addr === 0x4017,
        APU_READ:  (addr: number): boolean => addr === 0x4015,
        OAM_DMA:   (addr: number): boolean => addr === 0x4014,
        JOYPAD1_READ: (addr: number): boolean => addr === 0x4016,
        JOYPAD2_READ: (addr: number): boolean => addr === 0x4017,
        JOYPAD_WRITE: (addr: number): boolean => addr === 0x4016,
    } as const;

    private readonly systemRam: Uint8Array = new Uint8Array(2048);

    constructor(
        private readonly prgRam: Uint8Array,
        private readonly ppu: PPU,
        private readonly apu: APU,
        private readonly mapper: IMapper,
        private readonly joypad1: Joypad,
        private readonly joypad2: Joypad,
        private readonly triggerCpuStall: (cycles: number) => void,
    )
    {
    }

    public serialize(): Record<string, any>
    {
        return {
            systemRam: serializeMemory(this.systemRam),
        };
    }

    public deserialize(data: Record<string, any>): void
    {
        this.systemRam.set(deserializeMemory(data.systemRam));
    }

    public peek(address: number): number
    {
        address &= 0xFFFF;

        if (CPUBus.IsAddressing.SYS_RAM(address)) {
            return this.systemRam[address & 0x07FF]; // Mirror 2KB RAM.
        }

        if (CPUBus.IsAddressing.PRG_RAM(address)) {
            return this.prgRam[address - 0x6000];
        }

        if (CPUBus.IsAddressing.ROM(address)) {
            return this.mapper.peekCpu(address);
        }

        if (CPUBus.IsAddressing.PPU(address)) {
            return this.ppu.peekRegister(address & 0x0007);
        }

        if (CPUBus.IsAddressing.APU_READ(address)) {
            return this.apu.peekRegister(address);
        }

        if (CPUBus.IsAddressing.JOYPAD1_READ(address)) {
            return this.joypad1.peekState();
        }

        if (CPUBus.IsAddressing.JOYPAD2_READ(address)) {
            return this.joypad2.peekState();
        }

        return 0;
    }

    public read(address: number): number
    {
        address &= 0xFFFF;

        if (CPUBus.IsAddressing.SYS_RAM(address)) {
            return this.systemRam[address & 0x07FF]; // Mirror 2KB RAM.
        }

        if (CPUBus.IsAddressing.PRG_RAM(address)) {
            return this.prgRam[address - 0x6000];
        }

        if (CPUBus.IsAddressing.ROM(address)) {
            return this.mapper.readCpu(address);
        }

        if (CPUBus.IsAddressing.PPU(address)) {
            return this.ppu.readRegister(address & 0x0007);
        }

        if (CPUBus.IsAddressing.APU_READ(address)) {
            return this.apu.readRegister(address);
        }

        if (CPUBus.IsAddressing.JOYPAD1_READ(address)) {
            return this.joypad1.readState();
        }

        if (CPUBus.IsAddressing.JOYPAD2_READ(address)) {
            return this.joypad2.readState();
        }

        return 0;
    }

    public write(address: number, value: number): void
    {
        address &= 0xFFFF;

        if (CPUBus.IsAddressing.SYS_RAM(address)) {
            this.systemRam[address & 0x07FF] = value;
            return;
        }

        if (CPUBus.IsAddressing.PRG_RAM(address)) {
            this.prgRam[address - 0x6000] = value;
            return;
        }

        if (CPUBus.IsAddressing.ROM(address)) {
            this.mapper.writeCpu(address, value);
            return;
        }

        if (CPUBus.IsAddressing.PPU(address)) {
            this.ppu.writeRegister(address & 0x0007, value);
            return;
        }

        if (CPUBus.IsAddressing.APU_WRITE(address)) {
            this.apu.writeRegister(address, value);
            return;
        }

        if (CPUBus.IsAddressing.JOYPAD_WRITE(address)) {
            this.joypad1.writeStrobe(value);
            this.joypad2.writeStrobe(value);
            return;
        }

        if (CPUBus.IsAddressing.OAM_DMA(address)) {
            // High byte contains page address.
            const sourcePage: number = value << 8;

            for (let i = 0; i < 256; i++) {
                this.ppu.oamMemory[this.ppu.oamAddress] = this.read(sourcePage + i);
                this.ppu.oamAddress                     = (this.ppu.oamAddress + 1) & 0xFF;
            }

            this.triggerCpuStall(513);
            return;
        }
    }
}
